import { CheckCircleFilled, ClockCircleOutlined } from "@ant-design/icons"
import { Typography } from "antd"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { useGetSomeUsers } from "#hooks/newHooks/useGetUser"
import { getNow } from "#utils/dateHelpers"

const { Text } = Typography

/**
 * Red "URGENT" pill badge.
 * `size="sm"` for popover items, `size="md"` (default) for the modal.
 */
export const UrgentTag = ({ size = "md" }: { size?: "sm" | "md" }) => {
  const fontSize = size === "sm" ? 10 : 12
  const padding = size === "sm" ? "1px 6px" : "2px 8px"

  return (
    <Text
      strong
      style={{
        color: "#fff",
        background: "#ff4d4f",
        padding,
        borderRadius: 4,
        fontSize,
        width: "fit-content",
        lineHeight: "normal",
      }}
    >
      URGENT
    </Text>
  )
}

/**
 * Timestamp showing absolute time + fuzzy relative time.
 * Renders on a room-colored background so text is white/green.
 * `size="sm"` for popover items, `size="md"` (default) for the modal.
 */
export const FuzzyTimestamp = ({
  createdAt,
  size = "md",
}: {
  createdAt: string
  size?: "sm" | "md"
}) => {
  const [fuzzy, setFuzzy] = useState(() => getNow().to(createdAt))

  useEffect(() => {
    const interval = setInterval(() => {
      setFuzzy(getNow().to(createdAt))
    }, 60000)
    return () => clearInterval(interval)
  }, [createdAt])

  const created = getNow()
    .set("hour", new Date(createdAt).getHours())
    .set("minute", new Date(createdAt).getMinutes())
  const time = getNow().isSame(createdAt, "day")
    ? created.format("h:mm A")
    : fuzzy

  const fontSize = size === "sm" ? 11 : 14
  const fuzzyColor = size === "sm" ? "#52c41a" : "#b7eb8f"

  return (
    <Text style={{ fontSize, color: "rgba(255,255,255,0.85)" }}>
      <ClockCircleOutlined style={{ marginRight: size === "sm" ? 3 : 4 }} />
      {time} &middot;{" "}
      <Text strong style={{ fontSize, color: fuzzyColor }}>
        {fuzzy}
      </Text>
    </Text>
  )
}

/**
 * Green checkmark list of users who have acknowledged the request.
 * `size="sm"` for popover items, `size="md"` (default) for the modal.
 */
export const AcknowledgerList = ({
  ids,
  size = "md",
}: {
  ids: string[]
  size?: "sm" | "md"
}) => {
  const users = useGetSomeUsers(ids)

  const EXIT_DURATION_MS = 200
  const prevCountRef = useRef(users.length)
  const [displayCount, setDisplayCount] = useState(users.length)

  useEffect(() => {
    const prev = prevCountRef.current
    prevCountRef.current = users.length

    if (users.length >= prev) {
      setDisplayCount(users.length)
      return
    }

    const timer = setTimeout(() => {
      setDisplayCount(users.length)
    }, EXIT_DURATION_MS)
    return () => clearTimeout(timer)
  }, [users.length])

  const fontSize = size === "sm" ? 11 : 13
  const color = size === "sm" ? "#52c41a" : "#b7eb8f"
  const marginTop = size === "sm" ? 4 : 8
  const gap = size === "sm" ? 0 : 2
  const rowHeight = 20
  const targetHeight = displayCount * rowHeight

  return (
    <motion.div
      initial={false}
      animate={{
        height: targetHeight,
        marginTop: displayCount > 0 ? marginTop : 0,
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <Flex col gap={gap}>
        <AnimatePresence mode="popLayout">
          {users.map((u) => (
            <AnimateInFlex
              animation={ANIMATION_BY_KEY.leftSm}
              animationKey={u.id}
            >
              <Text key={u.id} style={{ fontSize, color }}>
                <CheckCircleFilled
                  style={{ marginRight: size === "sm" ? 3 : 4 }}
                />
                {u.name} is on their way
              </Text>
            </AnimateInFlex>
          ))}
        </AnimatePresence>
      </Flex>
    </motion.div>
  )
}
